<template>
    <PageHeader :crumbs="[{ label: $t('portal.sidebar.notifications') }]" />

    <div class="tw-p-2">
        <div class="tw-h-full tw-w-full tw-p-4 tw-bg-white tw-rounded tw-border tw-border-b-2 tw-min-h-[calc(100dvh-125px)]">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <h2 class="tw-font-semibold tw-text-gray-700">{{ $t('portal.notifications.my-notifications') }}
                    <span v-if="unread" class="tw-ml-2 tw-text-xs tw-bg-primary-500 tw-text-white tw-rounded-full tw-px-2 tw-py-0.5">{{ unread }}</span>
                </h2>
                <a-button v-if="unread" intent="simple" left-icon="solar:check-read-line-duotone" @click="markAll">{{ $t('portal.notifications.mark-all-read') }}</a-button>
            </div>

            <div v-if="loading" class="tw-py-8 tw-text-center tw-text-gray-400"><icon icon="line-md:loading-twotone-loop" class="tw-text-3xl" /></div>

            <ul v-else class="tw-space-y-2">
                <li v-for="n in items" :key="n.id"
                    class="tw-p-4 tw-rounded tw-border tw-border-solid tw-cursor-pointer tw-duration-200 hover:tw-shadow-sm"
                    :class="n.is_read ? 'tw-bg-white tw-border-gray-200' : 'tw-bg-primary-50/40 tw-border-primary-200'"
                    @click="open(n)">
                    <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
                        <div class="tw-flex tw-items-start tw-gap-3">
                            <div class="tw-p-2 tw-rounded-md tw-border tw-border-solid" :class="n.is_read ? 'tw-bg-gray-50 tw-border-gray-200' : 'tw-bg-primary-100 tw-border-primary-200'">
                                <icon icon="solar:bell-bing-bold-duotone" :class="n.is_read ? 'tw-text-gray-400' : 'tw-text-primary-500'" class="tw-text-xl" />
                            </div>
                            <div>
                                <p class="tw-font-semibold tw-text-gray-800">{{ n.title }}</p>
                                <p class="tw-text-sm tw-text-gray-500 tw-mt-0.5">{{ n.message }}</p>
                            </div>
                        </div>
                        <span class="tw-text-xs tw-text-gray-400 tw-whitespace-nowrap">{{ fmt.datetime(n.date) }}</span>
                    </div>
                </li>
                <li v-if="!items.length" class="tw-py-8 tw-text-center tw-text-gray-400">{{ $t('portal.notifications.none') }}</li>
            </ul>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { notifications, markRead, markAllRead } from '@/api/portal';
import PageHeader from '@/components/PageHeader.vue';
import { useFormat } from '@/composables/useFormat';
import { usePortalStore } from '@/stores';

const fmt = useFormat();
const portalStore = usePortalStore();
const items = ref([]);
const unread = ref(0);
const loading = ref(false);

async function load() {
    loading.value = true;
    try {
        const { data } = await notifications();
        items.value = data.notifications || [];
        unread.value = data.unread_count || 0;
        portalStore.setUnread(unread.value);
    } catch (e) { /* interceptor */ } finally {
        loading.value = false;
    }
}

const open = async (n) => {
    if (n.is_read) return;
    try {
        await markRead(n.id);
        n.is_read = true;
        unread.value = Math.max(0, unread.value - 1);
        portalStore.decrementUnread();
    } catch (e) { /* interceptor */ }
};

const markAll = async () => {
    try {
        await markAllRead();
        items.value = items.value.map((n) => ({ ...n, is_read: true }));
        unread.value = 0;
        portalStore.clearUnread();
    } catch (e) { /* interceptor */ }
};

load();
</script>
